/**
 * useGuideBoardDirectionLink
 *
 * 안내현황판(guideBoard) 1개를 선택한 뒤 "왼쪽 영역 → 오른쪽 영역" 순서로 박스를
 * 드래그하면, 각 박스 안에 든 주차구역(zone)을 그 현황판의 leftDirectionIds /
 * rightDirectionIds 로 설정한다. (반경 미사용 — 안내현황판은 반경으로 대상을
 * 정할 수 없어 사용자가 그린 영역만 기준으로 삼는다)
 *
 * 대상은 개별 주차면이 아니라 주차구역 폴리곤이다.
 * (구현: findZonesInRect → isZoneElement)
 *
 * 드래그는 canvas 컨테이너의 capture-phase 리스너로 가로채 paper 의 pan/select 와
 * 충돌하지 않게 한다. 박스는 useDragSelect 와 동일하게 fixed div 로 그린다.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { dia } from '@joint/core'
import {
  assignGuideBoardDirections,
  findZonesInRect,
  screenToWorld,
  type WorldRect,
  type GuideBoardDirectionResult,
} from '../../../map-editor-features/editor/lib/guideBoardDirectionRelations'

export type GuideBoardLinkPhase = 'idle' | 'left' | 'right'

interface UseGuideBoardDirectionLinkArgs {
  paper: dia.Paper | null
  graph: dia.Graph | null
  canvasRef: RefObject<HTMLDivElement>
  onComplete?: (result: GuideBoardDirectionResult) => void
  onError?: (message: string) => void
}

interface UseGuideBoardDirectionLinkResult {
  phase: GuideBoardLinkPhase
  active: boolean
  start: (guideBoardId: string) => void
  cancel: () => void
}

const LEFT_COLOR = '#3B82F6' // blue-500
const RIGHT_COLOR = '#10B981' // emerald-500
const MIN_DRAG_PX = 5

export function useGuideBoardDirectionLink({
  paper,
  graph,
  canvasRef,
  onComplete,
  onError,
}: UseGuideBoardDirectionLinkArgs): UseGuideBoardDirectionLinkResult {
  const [phase, setPhase] = useState<GuideBoardLinkPhase>('idle')
  const boardIdRef = useRef<string | null>(null)
  const leftZoneIdsRef = useRef<string[]>([])

  const draggingRef = useRef(false)
  const startRef = useRef({ x: 0, y: 0 })
  const boxRef = useRef<HTMLDivElement | null>(null)
  // 드래그로 선택돼 하이라이트된 주차구역 id 들 — 취소/재시작 시 정리용.
  const highlightedIdsRef = useRef<string[]>([])

  const removeBox = useCallback((): void => {
    if (boxRef.current) {
      boxRef.current.remove()
      boxRef.current = null
    }
  }, [])

  const highlightSpots = useCallback(
    (ids: string[], className: string): void => {
      if (!paper || !graph) return
      for (const id of ids) {
        const cell = graph.getCell(id)
        if (!cell) continue
        const view = paper.findViewByModel(cell as dia.Element)
        if (view?.el) view.el.classList.add(className)
      }
      highlightedIdsRef.current.push(...ids)
    },
    [paper, graph],
  )

  const clearHighlights = useCallback((): void => {
    if (paper && graph) {
      for (const id of highlightedIdsRef.current) {
        const cell = graph.getCell(id)
        if (!cell) continue
        const view = paper.findViewByModel(cell as dia.Element)
        if (view?.el) {
          view.el.classList.remove('gb-left-selected')
          view.el.classList.remove('gb-right-selected')
        }
      }
    }
    highlightedIdsRef.current = []
  }, [paper, graph])

  const start = useCallback(
    (guideBoardId: string): void => {
      clearHighlights()
      boardIdRef.current = guideBoardId
      leftZoneIdsRef.current = []
      setPhase('left')
    },
    [clearHighlights],
  )

  const cancel = useCallback((): void => {
    removeBox()
    clearHighlights()
    draggingRef.current = false
    boardIdRef.current = null
    leftZoneIdsRef.current = []
    setPhase('idle')
  }, [removeBox, clearHighlights])

  useEffect(() => {
    if (phase === 'idle' || !paper || !graph) return
    const el = canvasRef.current
    if (!el) return

    const color = phase === 'left' ? LEFT_COLOR : RIGHT_COLOR

    // paper 의 pan/select 로 전파되기 전에 가로챈다. JointJS 는 pointerdown 과
    // mousedown 둘 다 바인딩하므로 두 이벤트 모두 capture 에서 stopPropagation.
    const blockEvent = (e: Event): void => {
      e.preventDefault()
      e.stopPropagation()
    }

    const onDown = (e: PointerEvent): void => {
      blockEvent(e)
      if (draggingRef.current) return
      draggingRef.current = true
      startRef.current = { x: e.clientX, y: e.clientY }

      const box = document.createElement('div')
      box.style.position = 'fixed'
      box.style.border = `2px solid ${color}`
      box.style.backgroundColor = `${color}1a` // ~10% alpha
      box.style.pointerEvents = 'none'
      box.style.zIndex = '10000'
      box.style.left = `${e.clientX}px`
      box.style.top = `${e.clientY}px`
      box.style.width = '0px'
      box.style.height = '0px'
      document.body.appendChild(box)
      boxRef.current = box
    }

    const onMove = (e: PointerEvent): void => {
      if (!draggingRef.current || !boxRef.current) return
      const x = Math.min(e.clientX, startRef.current.x)
      const y = Math.min(e.clientY, startRef.current.y)
      const w = Math.abs(e.clientX - startRef.current.x)
      const h = Math.abs(e.clientY - startRef.current.y)
      boxRef.current.style.left = `${x}px`
      boxRef.current.style.top = `${y}px`
      boxRef.current.style.width = `${w}px`
      boxRef.current.style.height = `${h}px`
    }

    const onUp = (e: PointerEvent): void => {
      if (!draggingRef.current) return
      draggingRef.current = false
      removeBox()

      const dx = Math.abs(e.clientX - startRef.current.x)
      const dy = Math.abs(e.clientY - startRef.current.y)
      if (dx < MIN_DRAG_PX && dy < MIN_DRAG_PX) {
        // 너무 작은 드래그는 무시(클릭). 같은 단계 유지.
        return
      }

      const p1 = screenToWorld(paper, startRef.current.x, startRef.current.y)
      const p2 = screenToWorld(paper, e.clientX, e.clientY)
      const rect: WorldRect = { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y }
      const ids = findZonesInRect(graph, rect).map((z) => String(z.id))

      if (phase === 'left') {
        leftZoneIdsRef.current = ids
        highlightSpots(ids, 'gb-left-selected')
        setPhase('right')
        return
      }

      highlightSpots(ids, 'gb-right-selected')

      // phase === 'right' → 확정
      const boardId = boardIdRef.current
      const board = boardId ? (graph.getCell(boardId) as dia.Element | null) : null
      if (!board) {
        onError?.('안내현황판을 찾을 수 없습니다')
        cancel()
        return
      }
      const result = assignGuideBoardDirections(
        board,
        leftZoneIdsRef.current,
        ids,
      )
      boardIdRef.current = null
      leftZoneIdsRef.current = []
      setPhase('idle')
      onComplete?.(result)
    }

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') cancel()
    }

    // capture=true 로 paper 핸들러보다 먼저 잡는다. pointerdown 으로 드래그를
    // 처리하고, mousedown 은 paper 로 새지 않게 capture 에서 같이 막는다.
    el.addEventListener('pointerdown', onDown as EventListener, true)
    el.addEventListener('mousedown', blockEvent, true)
    document.addEventListener('pointermove', onMove as EventListener)
    document.addEventListener('pointerup', onUp as EventListener)
    document.addEventListener('keydown', onKey)
    const prevCursor = el.style.cursor
    el.style.cursor = 'crosshair'

    return () => {
      el.removeEventListener('pointerdown', onDown as EventListener, true)
      el.removeEventListener('mousedown', blockEvent, true)
      document.removeEventListener('pointermove', onMove as EventListener)
      document.removeEventListener('pointerup', onUp as EventListener)
      document.removeEventListener('keydown', onKey)
      el.style.cursor = prevCursor
      removeBox()
    }
  }, [phase, paper, graph, canvasRef, onComplete, onError, cancel, removeBox, highlightSpots])

  return { phase, active: phase !== 'idle', start, cancel }
}
